import { HealingOutlined } from "@mui/icons-material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { styles } from "../../commonStyle/formStyles";
import { fetchAdmission, resetCurrentAdmission, updateAdmission } from "../../redux/admissions/admissionsActions";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchDepartments } from "../../redux/department/departmentAction";
import { fetchFloors } from "../../redux/floor/floorAction";
import { fetchPatient, fetchPatients } from "../../redux/patient/patientAction";
import { fetchWards } from "../../redux/ward/wardAction";

const EditAdmissionDetails = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const [currentPage, setcurrentPage] = useState(1);
  const [attenderType, setAttenderType] = useState("");
  const [numberOfAttenders, setNumberOfAttenders] = useState(1);
  const { id } = useParams();
  
  const admissionData = useSelector(
    (state) => state?.admission?.fetchAdmission || {}
  );

  useEffect(() => {
    dispatch(fetchAdmission(id));
  }, [dispatch, id]);

  const [dialogProps, setDialogProps] = useState({
    title: "",
    message: "",
    type: "success",
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patient_id: "",
      block_id: "",
      floor_id: "",
      ward_id: "",
      department: "",
      attender_type: "",
      no_of_attenders: "",
      attender_name: "",
      attender_relation: "",
      attender_address: "",
      attender_phone: "",
      attender_name_2: "",
      attender_relation_2: "",
      attender_address_2: "",
      attender_phone_2: "",
    },
  });

  // Set form values when admission data is loaded
  useEffect(() => {
    if (admissionData?.data?.data) {
      const data = admissionData.data.data;
      
      setValue("patient_id", data.patient_id);
      setValue("block_id", data.block_id);
      setValue("floor_id", data.floor_id);
      setValue("ward_id", data.ward_id);
      setValue("department", data.department_id);
      setValue("attender_type", data.attender_type?.toString());
      setValue("no_of_attenders", data.number_attenders?.toString());
      
      // Set attender type and number of attenders state
      setAttenderType(data.attender_type?.toString());
      setNumberOfAttenders(data.number_attenders);
      
      // Set attender details if available
      if (data.Attenders && data.Attenders.length > 0) {
        // First attender
        setValue("attender_name", data.Attenders[1]?.attender_name || "");
        setValue("attender_relation", data.Attenders[1]?.relation?.toString() || "");
        setValue("attender_address", data.Attenders[1]?.address || "");
        setValue("attender_phone", data.Attenders[1]?.phone || "");
        
        // Second attender if exists
        if (data.Attenders.length > 1) {
          setValue("attender_name_2", data.Attenders[0]?.attender_name || "");
          setValue("attender_relation_2", data.Attenders[0]?.relation?.toString() || "");
          setValue("attender_address_2", data.Attenders[0]?.address || "");
          setValue("attender_phone_2", data.Attenders[0]?.phone || "");
        }
      }
    }
  }, [admissionData, setValue]);

  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const departmentList = useSelector((state) => state?.department?.fetchDepartments || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const patientList = useSelector(
    (state) => state?.patients?.fetchListPatients || {}
  );
  const admissionState = useSelector(
    (state) => state?.admission?.updateAdmission || {}
  );

  const watchBlock = watch("block");
  const watchWard = watch("ward");
  const watchFloor = watch("floor");
  const watchPatient = watch("patient_id");
  const watchAttenderType = watch("attender_type");
  const watchNoOfAttenders = watch("no_of_attenders");

  useEffect(() => {
    if (watchAttenderType) {
      setAttenderType(watchAttenderType);
    }
  }, [watchAttenderType]);

  useEffect(() => {
    if (watchNoOfAttenders) {
      const numAttenders = parseInt(watchNoOfAttenders, 10);
      if (!isNaN(numAttenders) && (numAttenders === 1 || numAttenders === 2)) {
        setNumberOfAttenders(numAttenders);
      }
    }
  }, [watchNoOfAttenders]);

  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem("block", watchBlock);
    }
    if (watchWard) {
      localStorage.setItem("ward", watchWard);
    }
    if (watchFloor) {
      localStorage.setItem("floor", watchFloor);
    }
  }, [watchBlock, watchWard, watchFloor]);

  useEffect(() => {
    const fetchData = async () => {
       await dispatch(fetchPatients());
       await dispatch(fetchDepartments());
       await dispatch(fetchBlocks());
      };
      fetchData();
  }, [dispatch]);

  useEffect(() => {
    const blockId = watchBlock;
    if (blockId) {
      dispatch(fetchFloors(blockId, currentPage));
    }
  }, [watchBlock, dispatch, currentPage]);

  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor, currentPage));
    }
  }, [watchBlock, watchFloor, dispatch, currentPage]);

  useEffect(() => {
    if (admissionState.success) {
      setDialogProps({
        title: "Success",
        message: "Patient has been successfully discharged.",
        type: "success",
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    } else if (admissionState.error) {
      setDialogProps({
        title: "Error",
        message: "Failed to discharge patient. Please try again.",
        type: "error",
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    }
  }, [admissionState, dispatch]);
  
  useEffect(() => {
    if (watchPatient) {
      dispatch(fetchPatient(watchPatient));
    }
  }, [watchPatient, dispatch]);

  const handleCancel = () => {
    reset();
  };

  const handleDischargeClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmDischarge = () => {
    const currentDate = new Date();
    const formattedDate = `${String(currentDate.getDate()).padStart(2, '0')}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${currentDate.getFullYear()} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}`;
    
    const formdata = {
      admission_status: "3",
      discharge_date: formattedDate
    };
    
    dispatch(updateAdmission(id, formdata));
    setConfirmDialogOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);

    if (dialogProps.type === "success") {
      reset();
    }
  };

  const patientFields = [
    {
      name: "patient_id",
      label: "Selected Patient",
      type: "select",
      options:
        patientList?.data?.data?.map((patient) => ({
          value: patient.id,
          label: patient.patient_name,
        })) || [],
      rules: { required: "Patient selection is required" },
      startIcon: <PersonIcon />,
      disabled: true
    },
  ];

  const currentWardFields = [
    {
      name: "block_id",
      label: "Block",
      placeholder: "Select block",
      type: "select",
      options: blockList?.Blocks?.data
        ? blockList.Blocks.data.map((block) => ({
            value: block.id,
            label: block.block_name,
          }))
        : [],
      rules: { required: "Block is required" },
      startIcon: <LocalHospitalIcon />,
      disabled: true
    },
    {
      name: "floor_id",
      label: "Floor",
      placeholder: "Select Floor",
      type: "select",
      options: floorList?.floors?.data
        ? floorList.floors.data.map((floor) => ({
            value: floor.id,
            label: floor.floor_name,
          }))
        : [],
      rules: { required: "Floor is required" },
      startIcon: <ApartmentIcon />,
      disabled: true
    },
    {
      name: "ward_id",
      label: "Ward Name",
      placeholder: "Select ward name",
      type: "select",
      options: wardList?.wards?.data
        ? wardList.wards.data.map((ward) => ({
            value: ward.id,
            label: ward.ward_name,
          }))
        : [],
      rules: { required: "Ward name is required" },
      startIcon: <MeetingRoomIcon />,
      disabled: true
    },
    {
      name: "department",
      label: "Department",
      placeholder: "Select department",
      type: "select",
      options: departmentList.departments 
        ? departmentList.departments.map(department => ({
            value: department.id,
            label: department.department_name
          })) 
        : [],
      rules: { required: "Department is required" },
      startIcon: <HealingOutlined />,
      disabled: true
    },
    {
      name: "attender_type",
      label: "Attender Type",
      placeholder: "Select attender type",
      type: "select",
      options: [
        { value: "1", label: "Known Attender" },
        { value: "2", label: "Unknown Attender" },
      ],
      rules: { required: "Attender type is required" },
      startIcon: <HealingOutlined />,
      disabled: true
    },
  ];

  // const attenderNumberField = [
  //   {
  //     name: "no_of_attenders",
  //     label: "Number of Attenders",
  //     placeholder: "Enter number of attenders (1 or 2)",
  //     type: "text",
  //     rules: { 
  //       required: "Number of attenders is required",
  //       pattern: {
  //         value: /^[1-2]$/,
  //         message: "Only 1 or 2 attenders are allowed"
  //       }
  //     },
  //     startIcon: <PersonIcon />,
  //     disabled: true
  //   },
  // ];



  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        <Typography variant="h4" sx={styles.heading}>
          View Admission Details
        </Typography>
      
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Patient Information
              </Typography>
              <ReusableFormFields
                inputFields={patientFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12 }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Location
              </Typography>
              <ReusableFormFields
                inputFields={currentWardFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12 }}
              />
            </Grid>
{/* 
            {watchAttenderType === "1" && (
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Number of Attenders
                </Typography>
                <ReusableFormFields
                  inputFields={attenderNumberField}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                  gridProps={{ xs: 12 }}
                />
              </Grid>
            )}

            Display attender details based on selected type and number 
            {watchAttenderType === "1" && watchNoOfAttenders === "1" && (
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  First Attender Details
                </Typography>
                <ReusableFormFields
                  inputFields={firstAttenderFields}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                  gridProps={{ xs: 12 }}
                />
              </Grid>
            )}

            {watchAttenderType === "1" && watchNoOfAttenders === "2" && (
              <>
                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    First Attender Details
                  </Typography>
                  <ReusableFormFields
                    inputFields={firstAttenderFields}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Second Attender Details
                  </Typography>
                  <ReusableFormFields
                    inputFields={secondAttenderFields}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>
              </>
            )} */}
            <Grid item xs={12}>
              <Box sx={styles.buttonsContainer}>
                <CustomButton
                  text="Back"
                  variant="contained"
                  size="small"
                  onClick={handleCancel}
                />
                <CustomButton
                  text="Discharge"
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={handleDischargeClick}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Message Dialog */}
      {/* <MessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={dialogProps.title}
        message={dialogProps.message}
        type={dialogProps.type}
      /> */}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Discharge"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to discharge this patient?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton 
            text="Cancel" 
            onClick={handleCloseConfirmDialog} 
            variant="outlined"
          />
          <CustomButton 
            text="Discharge" 
            onClick={handleConfirmDischarge} 
            variant="contained" 
            color="error" 
            autoFocus
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditAdmissionDetails;